import type { RangeFilterInput } from '../../../apis/graphql/types';
import { ComaprisonOperation, FilterOperator } from '../../../apis/graphql/types';
import { isExistenceComparisonOperation } from '../TableFilterBar/AdditionalFilters/utils';
import type { OperatorValueState } from './FilterRow/OperatorValueRangeControl/OperatorValueRangeControl';
import { filterOperatorToComparisonOperation } from './operatorQueryVariables';

const DEFAULT_MULTIPLIER = 1;

/**
 * Returns a function that converts a user-typed display value to the stored
 * unit by dividing by `displayMultiplier` (from FilterRangeConfig).
 *
 * Examples:
 *   - percentPacing (multiplier 100): user types 10 → stored 0.1
 *   - CPM (multiplier 1/1,000,000): user types 10 → stored 10,000,000
 *   - null/undefined multiplier: identity (no conversion)
 */
export function makeSaveConverter(displayMultiplier?: number | null): (v: number) => number {
  const m = displayMultiplier ?? DEFAULT_MULTIPLIER;
  return (v) => Number((v / m).toPrecision(12));
}

/**
 * Returns a function that converts a stored value back to the display unit by
 * multiplying by `displayMultiplier` (from FilterRangeConfig). Rounds to
 * `decimals` decimal places to eliminate float representation noise.
 *
 * Examples:
 *   - percentPacing (multiplier 100, decimals 0): stored 0.1 → display 10
 *   - CPM (multiplier 1/1,000,000, decimals 2): stored 10,000,000 → display 10
 *   - null/undefined multiplier: identity (no conversion)
 */
export function makeDisplayConverter(
  displayMultiplier?: number | null,
  decimals?: number | null
): (v: number) => number {
  const m = displayMultiplier ?? DEFAULT_MULTIPLIER;
  const d = decimals ?? 0;
  return (v) => Number((v * m).toFixed(d));
}

/**
 * Converts the UI's operator+value selection into the RangeFilterInput shape
 * that GlobalFilters and campaign-searcher expect.
 *
 * Sets minOperator / maxOperator so the exact operator is preserved in GlobalFilters
 * and can be restored losslessly by rangeFilterToOperatorValue.
 *
 * IS_EMPTY / IS_NOT_EMPTY (KATANA-1811) are unary: they ride minOperator with no bound, and any
 * value left in state is ignored. They are handled before the null-value guard, which would otherwise
 * turn them into { min: null, max: null }, a filter that filters nothing.
 *
 * Pass `convertToSave` when the filter stores values in a different unit than
 * what the user types. Build it with `makeSaveConverter(config.rangeConfig?.displayMultiplier)`.
 */
export function operatorValueToRangeFilter(
  state: OperatorValueState,
  convertToSave?: (v: number) => number
): RangeFilterInput {
  const { operator, value } = state;
  if (operator === FilterOperator.IsEmpty || operator === FilterOperator.IsNotEmpty) {
    return { min: null, minOperator: filterOperatorToComparisonOperation(operator), max: null };
  }
  if (operator === null || value === null) return { min: null, max: null };

  const v = convertToSave ? convertToSave(value) : value;
  const compOp = filterOperatorToComparisonOperation(operator);

  switch (operator) {
    case FilterOperator.GreaterThan:
    case FilterOperator.GreaterThanOrEqualTo:
      return { min: v, minOperator: compOp, max: null };
    case FilterOperator.LessThan:
    case FilterOperator.LessThanOrEqualTo:
      return { min: null, max: v, maxOperator: compOp };
    case FilterOperator.Equals:
    case FilterOperator.NotEquals:
      return { min: v, minOperator: compOp, max: null };
    default:
      throw new Error(`operatorValueToRangeFilter: unhandled operator "${operator}"`);
  }
}

const COMPARISON_OP_TO_FILTER_OPERATOR: Record<string, FilterOperator> = {
  [ComaprisonOperation.GreaterThan]: FilterOperator.GreaterThan,
  [ComaprisonOperation.GreaterThanOrEqual]: FilterOperator.GreaterThanOrEqualTo,
  [ComaprisonOperation.LessThan]: FilterOperator.LessThan,
  [ComaprisonOperation.LessThanOrEqual]: FilterOperator.LessThanOrEqualTo,
  [ComaprisonOperation.Equal]: FilterOperator.Equals,
  [ComaprisonOperation.NotEqual]: FilterOperator.NotEquals,
  [ComaprisonOperation.IsEmpty]: FilterOperator.IsEmpty,
  [ComaprisonOperation.IsNotEmpty]: FilterOperator.IsNotEmpty,
};

/**
 * Reverse-maps a committed RangeFilterInput back into OperatorValueState for
 * display when the panel re-opens.
 *
 * When minOperator / maxOperator are present (written by operatorValueToRangeFilter),
 * the exact operator is restored losslessly. Without them (legacy values), falls back
 * to positional inference: min-only → GTE, max-only → LTE, min===max → EQUALS.
 *
 * An existence operator (IS_EMPTY / IS_NOT_EMPTY) is restored with no value, and wins over any bound
 * that rides along with it, so reopening the panel or restoring a shareable link brings back the
 * operator rather than an empty row (KATANA-1811).
 *
 * Pass `convertToDisplay` when the stored value needs converting back to the
 * user-facing unit. Build it with `makeDisplayConverter(config.rangeConfig?.displayMultiplier, config.valueDecimals)`.
 */
export function rangeFilterToOperatorValue(
  filter: RangeFilterInput | null | undefined,
  convertToDisplay?: (v: number) => number
): OperatorValueState {
  if (!filter) return { operator: null, value: null };
  const { min, max, minOperator, maxOperator } = filter;
  const hasMin = min !== null && min !== undefined;
  const hasMax = max !== null && max !== undefined;

  if (isExistenceComparisonOperation(minOperator)) {
    return { operator: COMPARISON_OP_TO_FILTER_OPERATOR[minOperator as string], value: null };
  }

  const toDisplay = (v: number): number => (convertToDisplay ? convertToDisplay(v) : v);

  if (hasMin && hasMax && min === max) {
    const operator = (minOperator && COMPARISON_OP_TO_FILTER_OPERATOR[minOperator]) ?? FilterOperator.Equals;
    return { operator, value: toDisplay(min as number) };
  }
  if (hasMin && !hasMax) {
    const operator =
      (minOperator && COMPARISON_OP_TO_FILTER_OPERATOR[minOperator]) ?? FilterOperator.GreaterThanOrEqualTo;
    return { operator, value: toDisplay(min as number) };
  }
  if (!hasMin && hasMax) {
    const operator = (maxOperator && COMPARISON_OP_TO_FILTER_OPERATOR[maxOperator]) ?? FilterOperator.LessThanOrEqualTo;
    return { operator, value: toDisplay(max as number) };
  }
  return { operator: null, value: null };
}
